import { useEffect, useRef } from 'react';

// A return to the tab right after a refresh (focus and visibilitychange
// usually fire together) doesn't need another one.
const FOCUS_THROTTLE_MS = 5000;

/**
 * Keeps on-screen data current with what other people change, without a
 * reload: calls `refresh('timer')` every `intervalMs` while the tab is
 * visible, and `refresh('focus')` straight away when the user comes back to
 * the tab. Nothing runs while the tab is hidden.
 *
 * `refresh` is read through a ref, so passing a new function each render
 * doesn't restart the timer.
 */
export function useLiveRefresh(refresh, { intervalMs, enabled = true }) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;
    let lastRun = Date.now();
    const run = (trigger) => {
      lastRun = Date.now();
      refreshRef.current?.(trigger);
    };
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') run('timer');
    }, intervalMs);
    const onReturn = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRun < FOCUS_THROTTLE_MS) return;
      run('focus');
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [enabled, intervalMs]);
}
